import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const AccountInfoTab = ({ user, userProfile }) => {
  const { updateProfile } = useAuth();
  const [fullName, setFullName] = useState(userProfile?.full_name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleUpdateName = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const { error } = await updateProfile({ full_name: fullName });

    if (error) {
      setMessage({ type: 'error', text: error?.message || 'Failed to update name' });
    } else {
      setMessage({ type: 'success', text: 'Display name updated successfully' });
    }

    setLoading(false);
  };

  const handleUpdatePassword = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (newPassword?.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    // Update password
    const { error } = await supabase?.auth?.updateUser({ password: newPassword });

    if (error) {
      setMessage({ type: 'error', text: error?.message || 'Failed to update password' });
    } else {
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }

    setLoading(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Account Information
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Manage your personal account details and security settings
        </p>
      </div>

      {message?.text && (
        <div
          className={`p-4 rounded-lg ${
            message?.type === 'success' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
          }`}
        >
          {message?.text}
        </div>
      )}

      {/* Email Address (Read-only) */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Email Address
        </h3>
        <Input
          type="email"
          value={user?.email || ''}
          disabled
          label="Email"
          description="Your email address cannot be changed"
        />
      </div>

      {/* Display Name */}
      <form onSubmit={handleUpdateName}>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Display Name
        </h3>
        <div className="space-y-4">
          <Input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e?.target?.value)}
            label="Full Name"
            placeholder="Enter your full name"
          />
          <Button type="submit" loading={loading} disabled={!fullName || fullName === userProfile?.full_name}>
            Update Name
          </Button>
        </div>
      </form>

      {/* Password Change */}
      <form onSubmit={handleUpdatePassword}>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Change Password
        </h3>
        <div className="space-y-4">
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e?.target?.value)}
            label="Current Password"
            placeholder="Enter current password"
          />
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e?.target?.value)}
            label="New Password"
            placeholder="Enter new password (min 6 characters)"
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e?.target?.value)}
            label="Confirm New Password"
            placeholder="Confirm new password"
          />
          <Button
            type="submit"
            loading={loading}
            disabled={!currentPassword || !newPassword || !confirmPassword}
          >
            Update Password
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AccountInfoTab;